import { PropsWithChildren, useMemo, useState } from 'react';
import { Else, If, Then, When } from 'react-if';
import cns from 'classnames';
import { addDays } from 'date-fns';
import { ParseKeys } from 'i18next';
import { useRouter } from 'next/router';
import { Trans, useTranslation } from 'next-i18next';
import { useDialog } from '@zep/dialog';
import { Button, Checkbox } from '@zep/ui';
import { Colors } from '@zep/ui-tokens';
import { getChannelTalkSupportArticleUrl } from '@zep/utils';
import { fCurrency, fDateTimeByCountryCode } from '../../../../../utils';
import { capitalizeFirstLetter } from '../../subscriptionUtil';
import { PlanCheckoutSection } from '../atoms';
import { useSubscriptionPaymentContext, useSubscriptionPaymentLog } from '../hooks';
import { PaymentForeignSubmit } from './PaymentForeignSubmit';
import { PaymentSubmit } from './PaymentSubmit';
import S from './PaymentSummary.module.scss';
export const PaymentSummary = () => {
  const {
    t
  } = useTranslation(['subscription']);
  const dialog = useDialog();
  const {
    step,
    handleStep,
    isUpdated,
    updateStatus,
    subscriptionPeriod,
    isKR,
    amplitudeData,
    selectedPlanInfo,
    추가결제금액,
    isAnnuallyPayment
  } = useSubscriptionPaymentContext();
  const send = useSubscriptionPaymentLog('payment_subscription_next', false, isAnnuallyPayment ? 'Annual' : 'Monthly');
  const [isChecked, setCheckList] = useState({
    paymentInfo: false,
    termsOfService: false,
    termsOfRefund: false
  });
  const isAgree = (() => {
    return isUpdated ? isChecked.paymentInfo && isChecked.termsOfRefund : isChecked.paymentInfo && isChecked.termsOfService && isChecked.termsOfRefund;
  })();
  const handleCheck = (key: 'paymentInfo' | 'termsOfService' | 'termsOfRefund') => {
    setCheckList(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };
  const setNextStep = async () => {
    const isUpgrade = updateStatus.type === 'UP_GRADE';
    const isKRW = selectedPlanInfo.currency === 'KRW';
    const additionalPaymentPrice = 추가결제금액?.value ?? 0;
    if (isUpgrade && (isKRW && additionalPaymentPrice < 100 && additionalPaymentPrice > 0 || !isKRW && additionalPaymentPrice < 1 && additionalPaymentPrice > 0)) {
      await dialog.alert({
        title: t('subscription:payment.alertFailPay.title'),
        content: t('subscription:payment.paymentSummary.actionFailAlert', {
          minimumPrice: isKRW ? `100${t('subscription:currency.won')}` : '$1'
        })
      });
      return;
    }
    send(amplitudeData);
    handleStep(2);
  };
  return <div className={S.wrapper} data-sentry-component="PaymentSummary" data-sentry-source-file="PaymentSummary.tsx">
      <div className={S.content}>
        <div className={S.info}>
          <PlanCheckoutSection.Title data-sentry-element="unknown" data-sentry-source-file="PaymentSummary.tsx">
            {t('subscription:payment.summaryCommon.title')}
          </PlanCheckoutSection.Title>
          <Info data-sentry-element="Info" data-sentry-source-file="PaymentSummary.tsx" />
          <div className={S.message_wrapper}>
            <Notice data-sentry-element="Notice" data-sentry-source-file="PaymentSummary.tsx">
              {t('subscription:payment.summaryCommon.periodGuideTextWithCustomDays', {
              days: subscriptionPeriod
            })}
            </Notice>
            <When condition={updateStatus.type === 'UP_GRADE'} data-sentry-element="When" data-sentry-source-file="PaymentSummary.tsx">
              <Notice data-sentry-element="Notice" data-sentry-source-file="PaymentSummary.tsx">
                {t('subscription:payment.summaryUpgrade.timingGuideText')}
              </Notice>
            </When>
            <When condition={isUpdated} data-sentry-element="When" data-sentry-source-file="PaymentSummary.tsx">
              <UpdatedMessages data-sentry-element="UpdatedMessages" data-sentry-source-file="PaymentSummary.tsx" />
            </When>
          </div>
        </div>
        {/***************************/}
        <div className={S.divider} />
        {/***************************/}
        <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 20
      }}>
          <PaymentPrice data-sentry-element="PaymentPrice" data-sentry-source-file="PaymentSummary.tsx" />
          <When condition={step === 2} data-sentry-element="When" data-sentry-source-file="PaymentSummary.tsx">
            <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 10
          }}>
              <Checkbox checked={isChecked.paymentInfo} onChange={() => handleCheck('paymentInfo')} label={<PaymentInfoLabel />} circle labelPlacement="left" data-sentry-element="Checkbox" data-sentry-source-file="PaymentSummary.tsx" />
              <When condition={!isUpdated} data-sentry-element="When" data-sentry-source-file="PaymentSummary.tsx">
                <Checkbox checked={isChecked.termsOfService} onChange={() => handleCheck('termsOfService')} label={<TermsOfServiceLabel />} circle labelPlacement="left" data-sentry-element="Checkbox" data-sentry-source-file="PaymentSummary.tsx" />
              </When>
              <Checkbox checked={isChecked.termsOfRefund} onChange={() => handleCheck('termsOfRefund')} label={<TermsOfRefundLabel />} circle labelPlacement="left" data-sentry-element="Checkbox" data-sentry-source-file="PaymentSummary.tsx" />
            </div>
          </When>
        </div>
      </div>
      <div className={S.footer}>
        <If condition={step === 1} data-sentry-element="If" data-sentry-source-file="PaymentSummary.tsx">
          <Then data-sentry-element="Then" data-sentry-source-file="PaymentSummary.tsx">
            <Button isFullWidth size={'md'} disabled={!updateStatus.enabledSubmit} onClick={setNextStep} data-sentry-element="Button" data-sentry-source-file="PaymentSummary.tsx">
              {t('subscription:payment.summaryCommon.buttonNext')}
            </Button>
          </Then>
          <Else data-sentry-element="Else" data-sentry-source-file="PaymentSummary.tsx">
            {isKR ? <PaymentSubmit isAgree={isAgree} /> : <PaymentForeignSubmit isAgree={isAgree} />}
          </Else>
        </If>
      </div>
    </div>;
};
const Info = () => {
  const {
    selectedPlan,
    selectedMaxCcu,
    space,
    isAnnuallyPayment,
    updateStatus,
    추가결제금액,
    subscriptionPeriod
  } = useSubscriptionPaymentContext();
  const {
    t
  } = useTranslation(['subscription']);
  const nextBillingDate = (() => {
    const date = addDays(new Date(), subscriptionPeriod);
    date.setUTCHours(0, 0, 0, 0);
    return fDateTimeByCountryCode(date, space.countryCode);
  })();
  const isNextBillingDateVisible = updateStatus.type === 'NONE_UPGRADE' || updateStatus.type === 'UP_GRADE' && (추가결제금액?.value ?? 0) > 0;
  return <div style={{
    display: 'flex',
    flexDirection: 'column',
    gap: 6
  }} data-sentry-component="Info" data-sentry-source-file="PaymentSummary.tsx">
      <div className={S.flex_row}>
        <div className={S.flex_row_left}>
          {t('subscription:payment.summaryCommon.plan')}
        </div>
        <div className={S.bold}>{capitalizeFirstLetter(selectedPlan)}</div>
      </div>
      <div className={S.flex_row}>
        <div className={S.flex_row_left}>
          {t('subscription:payment.paymentPeriod.title')}
        </div>
        <div className={S.bold}>
          {t(isAnnuallyPayment ? 'subscription:pricing.selectPlan.annuallyPayment' : 'subscription:pricing.selectPlan.monthlyPayment')}
        </div>
      </div>
      <div className={S.flex_row}>
        <span className={S.flex_row_left}>
          {t('subscription:payment.summaryCommon.ccu')}
        </span>
        <span className={S.bold}>
          {selectedMaxCcu < 0 ? t('subscription:pricing.calculation.barUnlimited') : t('subscription:payment.summaryCommon.ccuContent', {
          ccu: selectedMaxCcu?.toString()
        })}
        </span>
      </div>
      <div className={S.flex_row}>
        <span className={S.flex_row_left}>
          {t('subscription:payment.summaryCommon.space')}
        </span>
        <span className={S.bold}>{space.name}</span>
      </div>
      <When condition={isNextBillingDateVisible} data-sentry-element="When" data-sentry-source-file="PaymentSummary.tsx">
        <div className={S.flex_row}>
          <span className={S.flex_row_left}>
            {t('subscription:payment.summaryCommon.nextBillingDate')}
          </span>
          <span className={S.bold}>{nextBillingDate}</span>
        </div>
      </When>
    </div>;
};
const Notice = ({
  children
}: PropsWithChildren) => {
  return <div className={S.notice} data-sentry-component="Notice" data-sentry-source-file="PaymentSummary.tsx">{children}</div>;
};
const UpdatedMessages = () => {
  const {
    updateStatus
  } = useSubscriptionPaymentContext();
  return <>
      {/* 메세지가 2개 이상인 경우 이면서 첫번째 인 메세지는 글자 컬러가 필요없습니다*/}
      {updateStatus.messages.map((m, index, all) => <span className={S.updated_message} key={m} style={{
      color: all.length > 1 === (index === 0) ? undefined : Colors.PrimaryDefault
    }}>
          {m}
        </span>)}
    </>;
};
const PaymentPrice = () => {
  const {
    t
  } = useTranslation(['subscription']);
  const {
    selectedPlanInfo,
    updateStatus,
    추가결제금액,
    currency,
    isAnnuallyPayment
  } = useSubscriptionPaymentContext();
  const isDowngrade = updateStatus.type === 'DOWN_GRADE';
  const priceInfo = isDowngrade ? null : selectedPlanInfo.priceInfo;
  const shouldShowVatBreakdown = !isDowngrade && (priceInfo?.vatAmount ?? 0) > 0;
  const currencyString = t('subscription:currency.won');
  const priceBeforeVatLabel = t('subscription:payment.summary.priceBeforeVat');
  const finalPaymentPriceLabel = t('subscription:payment.summary.finalPaymentAmount');
  const finalPriceString = isDowngrade ? fCurrency(0, currency, {
    currencyString
  }) : selectedPlanInfo.finalPriceString;
  return <>
      <When condition={selectedPlanInfo.isSales} data-sentry-element="When" data-sentry-source-file="PaymentSummary.tsx">
        <div className={S.flex_column}>
          <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 12
        }}>
            <div className={S.flex_row}>
              <div className={S.flex_row_left}>
                {t('subscription:payment.summary.originPrice')}
              </div>
              <div className={S.bold_14}>
                {fCurrency(selectedPlanInfo.originalPrice, currency, {
                currencyString: t('subscription:currency.won')
              })}
              </div>
            </div>
          </div>
          <SaleContent data-sentry-element="SaleContent" data-sentry-source-file="PaymentSummary.tsx" />
        </div>
        <div className={S.divider} />
      </When>
      <div className={S.price_wrapper}>
        <When condition={추가결제금액 && updateStatus.type == 'UP_GRADE'} data-sentry-element="When" data-sentry-source-file="PaymentSummary.tsx">
          <div className={S.price}>
            {t('subscription:payment.summary.morePrice')}
            &nbsp;
            {(추가결제금액?.value ?? 0) > 0 && '(' + t('subscription:payment.summary.morePriceHelpText') + ')'}
            <div className={S.info_price}>
              <strong>{추가결제금액?.localString}</strong>
            </div>
          </div>
        </When>
        <When condition={shouldShowVatBreakdown} data-sentry-element="When" data-sentry-source-file="PaymentSummary.tsx">
          <div className={S.price}>
            <div>{priceBeforeVatLabel}</div>
            <div className={S.info_price}>
              <strong>
                {fCurrency(priceInfo?.priceBeforeVat ?? 0, currency, {
                currencyString
              })}
              </strong>
            </div>
          </div>
          <div className={S.price}>
            <div>{t('subscription:payment.summary.vat')}</div>
            <div className={S.info_price}>
              <strong>
                {fCurrency(priceInfo?.vatAmount ?? 0, currency, {
                currencyString
              })}
              </strong>
            </div>
          </div>
        </When>
        <div className={S.price}>
          <div>
            {shouldShowVatBreakdown ? finalPaymentPriceLabel : t('subscription:payment.summaryCommon.price')}
            &nbsp;
            {추가결제금액 && (updateStatus.type === 'DOWN_GRADE' || updateStatus.type === 'UP_GRADE') && '(' + t('subscription:payment.summaryCommon.priceHelpText') + ')'}
          </div>
          <div className={S.info_price}>
            <strong>{finalPriceString}</strong>
            <span>
              &nbsp;/&nbsp;
              {t(isAnnuallyPayment ? 'subscription:payment.summaryCommon.yearly' : 'subscription:payment.summaryCommon.monthly')}
            </span>
          </div>
        </div>
      </div>
    </>;
};
const SaleContent = () => {
  const {
    t
  } = useTranslation(['subscription']);
  const {
    selectedPlanInfo,
    currency,
    isAnnuallyPayment,
    annualSaleRate
  } = useSubscriptionPaymentContext();
  const saleString = useMemo(() => {
    return isAnnuallyPayment ? 'subscription:pricing.plansCommon.annuallyPayment' : `subscription:pricing.plansCommon.${selectedPlanInfo.saleType}` as ParseKeys<['subscription']>;
  }, [isAnnuallyPayment, selectedPlanInfo.saleType]);
  return <div className={S.flex_row} style={{
    alignItems: 'flex-start'
  }} data-sentry-component="SaleContent" data-sentry-source-file="PaymentSummary.tsx">
      <div className={S.flex_row_left}>
        {t('subscription:payment.summary.saleContent')}
      </div>
      <div style={{
      display: 'flex',
      flexDirection: 'column',
      gap: 5,
      alignItems: 'flex-start'
    }}>
        <div className={cns(S.bold_14, S.text_align_left)}>
          {t('subscription:payment.summary.saleText', {
          saleType: t(saleString)
        })}
        </div>
        <div style={{
        display: 'flex',
        gap: 4,
        alignItems: 'center',
        textAlign: 'center'
      }}>
          <div className={S.sale_price_box}>
            {selectedPlanInfo.isAnnualSales ? annualSaleRate : selectedPlanInfo.saleDiscountRate}
            %
          </div>
          <div className={S.bold_14} style={{
          height: '100%',
          display: 'flex'
        }}>
            -
            {fCurrency(selectedPlanInfo.saleDiscountPrice, currency, {
            currencyString: t('subscription:currency.won')
          })}
          </div>
        </div>
      </div>
    </div>;
};
const PaymentInfoLabel = () => {
  const {
    t
  } = useTranslation(['subscription']);
  return <span className={S.checkbox_label} data-sentry-component="PaymentInfoLabel" data-sentry-source-file="PaymentSummary.tsx">
      {t('subscription:payment.summaryCommon.checkboxPaymentInfo')}
    </span>;
};
const TermsOfServiceLabel = () => {
  const {
    locale = 'ko'
  } = useRouter();
  const {
    t
  } = useTranslation(['subscription']);
  const serviceTerms = t('subscription:checkout.service.terms');
  return <span className={S.checkbox_label} data-sentry-component="TermsOfServiceLabel" data-sentry-source-file="PaymentSummary.tsx">
      <Trans t={t} i18nKey={'subscription:payment.summaryCommon.checkboxTerms'} values={{
      serviceTerms
    }} components={{
      S: <a className={S.link} href={getChannelTalkSupportArticleUrl(locale, 'b40ac18f')} target="_blank" rel="noreferrer" />
    }} data-sentry-element="Trans" data-sentry-source-file="PaymentSummary.tsx" />
    </span>;
};
const TermsOfRefundLabel = () => {
  const {
    t
  } = useTranslation(['subscription']);
  const refundLaws = t('subscription:checkout.refund.laws');
  return <span className={S.checkbox_label} data-sentry-component="TermsOfRefundLabel" data-sentry-source-file="PaymentSummary.tsx">
      <Trans t={t} i18nKey={'subscription:payment.summaryCommon.checkboxRefund'} values={{
      refundLaws
    }} components={{
      R: <a className={S.link} href={t('subscription:checkout.refund.premiumGuideLink')} target="_blank" rel="noreferrer" />
    }} data-sentry-element="Trans" data-sentry-source-file="PaymentSummary.tsx" />
    </span>;
};